import React, { memo, useCallback, useEffect } from 'react';
import { Box, Typography, Grid, MenuItem } from '@mui/material';
import { useFormik } from 'formik';
import FormTextField from './FormTextField';
import FormSelectField from './FormSelectField';
import FormCheckbox from './FormCheckbox';
import { fiscalDataSchema } from './validationSchema';

const defaultInitialValues = {
  documentType: '',
  cuitCuil: '',
};

const FiscalDataSection = memo(
  ({
    index,
    background,
    useCommonFiscalData,
    commonFiscalDataPassengerIndex,
    commonFiscalData,
    onToggleCommonData,
    handleCommonFiscalDataChange,
    handleIndividualFiscalDataChange,
    updateFiscalData,
    initialValues,
    totalPassengers,
    isInternational,
    countryCode,
  }) => {
    // Si no es un vuelo internacional, no mostrar la sección
    if (!isInternational) {
      return null;
    }

    const safeInitialValues = initialValues || defaultInitialValues;

    const formik = useFormik({
      initialValues: useCommonFiscalData ? commonFiscalData : safeInitialValues,
      validationSchema: fiscalDataSchema,
      validateOnMount: false,
      validateOnChange: true,
      validateOnBlur: true,
    });

    // Función para formatear CUIT/CUIL
    const formatCUITCUIL = (value) => {
      if (!value) return value;
      const numbers = value.replace(/\D/g, '');
      if (numbers.length <= 2) return numbers;
      if (numbers.length <= 10) {
        return `${numbers.slice(0, 2)}-${numbers.slice(2)}`;
      }
      return `${numbers.slice(0, 2)}-${numbers.slice(2, 10)}-${numbers.slice(10, 11)}`;
    };

    // Actualizar valores cuando cambian los datos comunes o cuando se desactiva el uso de datos comunes
    useEffect(() => {
      if (useCommonFiscalData) {
        formik.setValues({
          documentType: commonFiscalData.documentType,
          cuitCuil: commonFiscalData.cuitCuil,
        });

        // Cuando usamos datos comunes, validar el formulario para mostrar errores
        if (index === commonFiscalDataPassengerIndex) {
          formik.validateForm().then((errors) => {
            const hasErrors = Object.keys(errors).length > 0;
            // Si hay errores en el pasajero principal, actualizar todos los pasajeros
            if (hasErrors) {
              handleCommonFiscalDataChange('validationStatus', hasErrors);
            }
          });
        }
      } else if (initialValues) {
        // Cuando se desactiva useCommonFiscalData, volver a los valores iniciales específicos de este pasajero
        formik.setValues({
          documentType: initialValues.documentType || '',
          cuitCuil: initialValues.cuitCuil || '',
        });

        // Resetear toques y errores cuando cambia el estado
        if (formik.touched.documentType || formik.touched.cuitCuil) {
          formik.setTouched({
            documentType: false,
            cuitCuil: false,
          });
        }
      }
    }, [
      useCommonFiscalData,
      commonFiscalData,
      index,
      commonFiscalDataPassengerIndex,
      initialValues,
    ]);

    const handleChange = useCallback(
      (e) => {
        const { name, value } = e.target;

        // Si es el campo CUIT/CUIL, formatear el valor
        if (name === 'cuitCuil') {
          const formattedValue = formatCUITCUIL(value);
          formik.setFieldValue(name, formattedValue, true);

          if (useCommonFiscalData) {
            handleCommonFiscalDataChange(name, formattedValue);
          } else {
            handleIndividualFiscalDataChange(index, name, formattedValue);
          }
        } else if (name === 'documentType') {
          // Al cambiar el tipo de documento, mantener el número actual
          const currentCuitCuil = formik.values.cuitCuil;
          formik.setFieldValue(name, value, true);

          if (useCommonFiscalData) {
            handleCommonFiscalDataChange(name, value);
            // Mantener el número actual en los datos comunes
            handleCommonFiscalDataChange('cuitCuil', currentCuitCuil);
          } else {
            handleIndividualFiscalDataChange(index, name, value);
            // Mantener el número actual en los datos individuales
            handleIndividualFiscalDataChange(index, 'cuitCuil', currentCuitCuil);
          }
        } else {
          formik.setFieldValue(name, value, true);

          if (useCommonFiscalData) {
            handleCommonFiscalDataChange(name, value);
          } else {
            handleIndividualFiscalDataChange(index, name, value);
          }
        }
      },
      [
        useCommonFiscalData,
        handleCommonFiscalDataChange,
        handleIndividualFiscalDataChange,
        index,
        formik,
      ]
    );

    const handleBlur = useCallback(
      (e) => {
        const { name } = e.target;
        formik.handleBlur(e);
        formik.setFieldTouched(name, true, true);

        // Validar después de blur y actualizar el estado de validación
        formik.validateField(name).then(() => {
          const { errors } = formik;
          const hasErrors = Object.keys(errors).length > 0;

          if (useCommonFiscalData && index === commonFiscalDataPassengerIndex) {
            // Propagar el estado de validación a través de los datos comunes
            handleCommonFiscalDataChange('validationStatus', hasErrors);
          } else if (!useCommonFiscalData) {
            // Actualizar solo este pasajero
            const updatedData = {
              documentType: formik.values.documentType,
              documentNumber: formik.values.documentNumber,
              cuitCuil: formik.values.cuitCuil,
            };
            updateFiscalData(index, updatedData, false, hasErrors);
          }
        });
      },
      [
        formik,
        index,
        useCommonFiscalData,
        commonFiscalDataPassengerIndex,
        handleCommonFiscalDataChange,
        updateFiscalData,
      ]
    );

    const handleToggleCommonData = useCallback(
      (e) => {
        const isChecked = e.target.checked;
        const currentValues = formik.values;

        // Validar el formulario actual antes de compartir datos
        formik.validateForm().then((errors) => {
          const hasErrors = Object.keys(errors).length > 0;
          onToggleCommonData(index, isChecked ? currentValues : null, hasErrors);
        });
      },
      [index, formik, onToggleCommonData]
    );

    // Determinar si se deben mostrar errores
    const showErrors = useCommonFiscalData
      ? index === commonFiscalDataPassengerIndex // Solo mostrar errores en el pasajero principal cuando se usan datos comunes
      : true; // Mostrar errores individuales cuando no se usan datos comunes

    return (
      <>
        <Grid item xs={12} data-testid='fiscal-data-container'>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid',
              borderColor: 'divider',
              pb: 1,
              mb: 2,
            }}
          >
            <Typography sx={{ fontSize: '1.1rem' }} variant='h6'>
              Datos fiscales <span style={{ color: 'red' }}>*</span>
            </Typography>
            {totalPassengers > 1 && (
              <>
                {useCommonFiscalData && commonFiscalDataPassengerIndex !== index ? (
                  <Typography
                    sx={{ fontStyle: 'italic', color: 'text.secondary', mb: 2 }}
                    variant='body1'
                  >
                    Se está usando los datos del Pasajero {commonFiscalDataPassengerIndex + 1}
                  </Typography>
                ) : (
                  <FormCheckbox
                    background={background}
                    checked={useCommonFiscalData}
                    label={
                      useCommonFiscalData
                        ? `Usar datos del Pasajero ${commonFiscalDataPassengerIndex + 1}`
                        : 'Usar datos en todos los pasajeros'
                    }
                    onChange={handleToggleCommonData}
                  />
                )}
              </>
            )}
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormSelectField
                disabled={useCommonFiscalData && index !== commonFiscalDataPassengerIndex}
                error={
                  showErrors && formik.touched.documentType && Boolean(formik.errors.documentType)
                }
                formik={formik}
                helperText={
                  showErrors && formik.touched.documentType ? formik.errors.documentType : ''
                }
                label='Tipo *'
                name='documentType'
                onBlur={handleBlur}
                onChange={handleChange}
              >
                <MenuItem value='CUIL'>CUIL</MenuItem>
                <MenuItem value='CUIT'>CUIT</MenuItem>
              </FormSelectField>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormTextField
                disabled={
                  (useCommonFiscalData && index !== commonFiscalDataPassengerIndex) ||
                  !formik.values.documentType
                }
                error={showErrors && formik.touched.cuitCuil && Boolean(formik.errors.cuitCuil)}
                formik={formik}
                helperText={
                  (showErrors && formik.touched.cuitCuil && formik.errors.cuitCuil) ||
                  'Formato: XX-XXXXXXXX-X'
                }
                inputProps={{ maxLength: 13 }}
                inputType='fiscal'
                label='Número *'
                name='cuitCuil'
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  }
);

FiscalDataSection.displayName = 'FiscalDataSection';

export default FiscalDataSection;
